import {
  DEFAULT_IMAGERY_PROVIDER,
  FRAME_SIZE_PRESETS,
  GEOCODER_ATTRIBUTION,
  GeocodeError,
  IMAGERY_PROVIDERS,
  ImageryError,
  ImageryKeyStore,
  MAX_FRAME_METERS,
  MAX_VIEW_ZOOM,
  MIN_FRAME_METERS,
  MIN_VIEW_ZOOM,
  SITE_DATA_SOURCES,
  boundaryBounds,
  browseProvider,
  captureFrameImage,
  createFrame,
  describeArea,
  CORNER_NAMES,
  describeFrame,
  describeGrid,
  findPlace,
  frameBounds,
  frameEdgeMeters,
  frameForBounds,
  frameIsRectangular,
  frameSkew,
  gridCoverage,
  gridToControlPoints,
  gridToMeasuredGround,
  imageryProvider,
  measureBoundary,
  moveCorner,
  panFrame,
  panView,
  parseBoundaryGeoJson,
  parseTopographyFile,
  rotateFrame,
  screenToView,
  simulateParcel,
  spotHeightsToControlPoints,
  squareUpFrame,
  tileScreenBox,
  tileUrl,
  topographyToArtifact,
  viewForFrame,
  viewMetersPerPixel,
  viewToScreen,
  visibleTiles,
  zoomViewAt,
  type ElevationGrid,
  type LatLng,
  type MapFrame,
  type MapView,
  type ParcelBoundary,
  type PlaceResult,
  type ScreenPoint,
  type ViewportSize,
} from '@/geo';
import {
  OVERPASS_MIRRORS,
  describeStructureReport,
  osmToStructures,
  overpassQuery,
  parseOverpass,
  type StructureImportReport,
} from '@/geo/capture';
import type { StructureArtifact, TerrainArtifact, TerrainControlPoint } from '@/reference';
import { append, clear, el, formatLength } from './dom';
import { icon } from './Icons';
import { dialogButton, textInput } from './Dialog';

/**
 * Select Frame: browse to the plot on a map and take the reference image from it.
 *
 * ## What this replaces
 *
 * Four manual steps — open a map site, hunt for the address, screenshot it, crop and upload — and,
 * more importantly, the *unknown* that came out the far end. A hand-taken screenshot arrives with no
 * scale, no orientation and no coordinates, which is why stage 1's first job was to guess metres per
 * pixel off a car in the driveway and why every number downstream inherited that guess. A frame
 * chosen here carries its own projection, so the site extent is measured. See `geo/MapFrame.ts`.
 *
 * ## Two pieces of state, and they do not touch
 *
 * This is the thing the first version got wrong, and it is worth stating before anything else.
 *
 * - **The view** (`geo/MapView.ts`) — where you are looking. Panning and zooming move it.
 * - **The frame** (`geo/MapFrame.ts`) — the rectangle of ground you are claiming as the site. It
 *   sits *on* the ground and stays where it is put.
 *
 * Originally the view was computed from the frame — "the frame, times 2.2" — which made every
 * gesture fight itself: widening the quad widened the view drawn from it, so the ground under the
 * cursor rescaled mid-drag and the corner could never reach where it was aimed; and every pointer
 * move rebuilt the pane, so the imagery blanked and re-requested dozens of times a second. Separating
 * them is what makes all of it behave. `geo/MapView.ts` carries the full account.
 *
 * ## The gestures, and there are only four
 *
 * | Gesture | What it does |
 * | ------- | ------------ |
 * | Drag outside the frame | move the map |
 * | Drag inside the frame | move the frame over the ground |
 * | Drag a green corner | reshape the frame |
 * | Wheel / pinch | zoom the map about the cursor |
 *
 * ## Three panes, in the order the work happens
 *
 * 1. **Where** — search or paste coordinates, then place the frame over the plot. The view
 *    deliberately shows more ground than the frame does: choosing a boundary while blind to what is
 *    just outside it is how a site ends up cropped through its own hedge.
 * 2. **What is known** — the parcel boundary and the topography. Public data where it exists, a file
 *    where it does not, and a clearly-labelled simulation where neither is wired yet
 *    (`geo/SiteData.ts` carries the full list and what each real source would take).
 * 3. **Capture** — fetch the frame at full resolution and hand it to the pipeline as the reference
 *    photograph, with its georeference attached.
 *
 * Uploading your own photograph stays available throughout, and remains the only option for a site
 * that has no useful aerial coverage — under tree canopy, a courtyard, a roof terrace.
 */

/** Where the map opens when nothing else is known: the middle of nowhere is worse than a landmark. */
const DEFAULT_CENTER: LatLng = { lat: 42.3601, lng: -71.0589 };
const DEFAULT_SIZE_METERS = 60;

/** Before the pane has been measured. Replaced by the real size on the first `ResizeObserver` call. */
const ASSUMED_PANE: ViewportSize = { width: 560, height: 560 };

/** A drag has to move this far before it counts as a drag rather than a click. */
const DRAG_SLOP_PIXELS = 2;

export interface FrameSelectorDeps {
  /** The frame was captured. The file is the reference photograph; the frame is its georeference. */
  onCapture: (file: File, frame: MapFrame) => void;
  /** Imported topography is ready to stand in for the terrain stage. */
  onTerrain?: (artifact: TerrainArtifact, frame: MapFrame) => void;
  /**
   * Measured buildings, walls and fences, with the evidence that produced them.
   *
   * `detail` is the import report as text and takes the place of a model's raw reply, so the
   * inspector's Reply pane answers "why is there a shed there" the same way whether a survey or a
   * model put it there.
   */
  onStructures?: (artifact: StructureArtifact, detail: string, frame: MapFrame) => void;
  /** Anything the frame changed — the host redraws its own readiness. */
  onChange?: () => void;
  keyStore?: ImageryKeyStore;
}

export class FrameSelector {
  readonly root: HTMLElement;

  private readonly keys: ImageryKeyStore;

  private providerId = DEFAULT_IMAGERY_PROVIDER;
  /** Measured structures and the report that explains them. Null until Overpass has answered. */
  private structures: { artifact: StructureArtifact; report: StructureImportReport } | null = null;
  private fetchingStructures = false;

  private frame: MapFrame;
  private view: MapView;
  private sizeMeters = DEFAULT_SIZE_METERS;

  private query = '';
  private results: PlaceResult[] = [];
  private searching = false;

  private boundary: ParcelBoundary | null = null;
  private grid: ElevationGrid | null = null;
  private spotHeights: TerrainControlPoint[] | null = null;
  private topographyLabel = '';

  private status: { message: string; tone: 'info' | 'error' } | null = null;
  private capturing = false;
  private abort: AbortController | null = null;

  /*
   * The map's DOM is built once and kept, rather than rebuilt by `render()`.
   *
   * That is not an optimisation — it is the fix. A rebuilt `<img>` is a fresh network request and a
   * blank box until it lands, and the old code did one per pointer move. Nothing below re-creates
   * the pane; gestures call `layout()`, which moves existing nodes.
   */
  private readonly mapWrap: HTMLElement;
  private readonly mapEl: HTMLElement;
  private readonly tileLayer: HTMLElement;
  private readonly overlay: SVGSVGElement;
  private readonly howto: HTMLElement;
  private readonly attribution: HTMLElement;
  private readonly emptyNote: HTMLElement;

  /** Live tile elements, keyed `z/x/y` — so a tile survives a gesture instead of being refetched. */
  private readonly tiles = new Map<string, HTMLImageElement>();
  private pendingTiles = 0;

  private pane: ViewportSize = ASSUMED_PANE;
  private paneMeasured = false;
  private readonly paneObserver: ResizeObserver | null;

  constructor(private readonly deps: FrameSelectorDeps) {
    this.keys = deps.keyStore ?? new ImageryKeyStore();
    this.frame = createFrame({
      center: DEFAULT_CENTER,
      sizeMeters: this.sizeMeters,
      providerId: this.providerId,
    });
    this.view = viewForFrame(this.frame, this.pane);

    this.tileLayer = el('div', { class: 'lg-frame__tiles' });
    this.overlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.overlay.setAttribute('class', 'lg-frame__quad');
    this.emptyNote = el('div', { class: 'lg-frame__mapempty' });
    this.mapEl = el('div', { class: 'lg-frame__map' });
    append(this.mapEl, this.tileLayer, this.overlay, this.emptyNote);

    this.howto = el('div', { class: 'lg-frame__howto' });
    this.attribution = el('p', { class: 'lg-frame__attribution' });
    this.mapWrap = el('div', { class: 'lg-frame__mapwrap' }, this.howto, this.mapEl, this.attribution);

    this.mapEl.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    // Explicitly non-passive: the browser's default wheel action here is to scroll the dialog, and
    // a map that scrolls the page out from under the cursor instead of zooming is the single most
    // common way a map widget feels broken.
    this.mapEl.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });

    this.root = el('div', { class: 'lg-frame', attrs: { 'aria-label': 'Select the site frame' } });
    this.paneObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver((entries) => this.onPaneResize(entries))
        : null;
    this.paneObserver?.observe(this.mapEl);

    this.render();
  }

  /** The frame currently chosen, whether or not it has been captured. */
  get currentFrame(): MapFrame {
    return this.frame;
  }

  dispose(): void {
    this.abort?.abort();
    this.paneObserver?.disconnect();
    this.root.remove();
  }

  /* --------------------------------- Rendering --------------------------------- */

  /**
   * Rebuild the panel around the map.
   *
   * Everything here is cheap DOM. The map itself is `this.mapWrap`, built once in the constructor
   * and merely re-appended, so a `render()` never costs an imagery request. Gestures do not call
   * this at all — they call {@link layout}.
   */
  private render(): void {
    clear(this.root);
    append(
      this.root,
      // The map and its search box are one column, everything else is the other. The map is the
      // instrument — you are choosing a boundary by *looking* — so on any window wide enough it
      // takes the height of the pane, and the settings that describe what it is showing sit beside
      // it rather than pushing it into a strip at the top.
      el('div', { class: 'lg-frame__stage' }, this.searchRow(), this.mapWrap),
      el(
        'div',
        { class: 'lg-frame__panel' },
        this.frameControls(),
        this.knownSection(),
        this.captureRow(),
      ),
    );
    this.layout();
  }

  /**
   * Put the map where the state says it should be. Called on every frame of every gesture.
   *
   * Tiles are positioned, not created; the overlay's handful of SVG nodes are rebuilt, which is free.
   * No network, no image decode, nothing that can blank.
   */
  private layout(): void {
    this.layoutTiles();
    this.drawOverlay();
    this.refreshHowTo();
  }

  private searchRow(): HTMLElement {
    const field = textInput({
      value: this.query,
      placeholder: 'Address, “42.3601, -71.0589”, or a Google Maps link',
      ariaLabel: 'Find a place',
      onInput: (value) => {
        this.query = value;
      },
      onSubmit: () => void this.search(),
    });

    const row = el(
      'div',
      { class: 'lg-frame__search' },
      el('span', { class: 'lg-frame__searchicon', html: icon('search', 15) }),
      field,
      dialogButton({
        label: this.searching ? 'Searching…' : 'Find',
        onClick: () => void this.search(),
        disabled: this.searching,
      }),
    );

    if (this.results.length > 1) {
      const list = el('div', { class: 'lg-frame__results' });
      for (const result of this.results) {
        list.appendChild(
          el('button', {
            class: 'lg-frame__result',
            text: result.label,
            title: `${result.kind} · ${result.at.lat.toFixed(5)}, ${result.at.lng.toFixed(5)}`,
            attrs: { type: 'button', 'data-autofocus': 'skip' },
            on: { click: () => this.goTo(result.at) },
          }),
        );
      }
      return el('div', { class: 'lg-frame__searchwrap' }, row, list);
    }
    return row;
  }

  /* ----------------------------------- The map ----------------------------------- */

  /**
   * The tile layer.
   *
   * A tile's identity is `z/x/y`, so the same tile keeps the same element and the same browser cache
   * entry for as long as it is on screen. Panning across a plot costs a handful of new tiles;
   * panning back costs none. The layer this replaced re-requested a whole 1280 px image on every
   * pointer event, which is why it fell over.
   *
   * Tiles from the previous zoom level are kept underneath until the new level has finished loading.
   * Without that, crossing a rounding boundary flashes the map grey.
   */
  private layoutTiles(): void {
    const chosen = imageryProvider(this.providerId);
    const provider = browseProvider(chosen);
    const key = this.keys.get(provider.id) ?? undefined;

    if (provider.browse === 'xyz' && provider.id === 'mapbox-satellite' && !key) {
      this.setEmptyNote(
        `${provider.name} needs a ${provider.keyLabel ?? 'key'} before it can draw. Add one below, ` +
          'or switch to a provider that needs none.',
      );
      return;
    }
    this.setEmptyNote(null);

    const wanted = visibleTiles(this.view, this.pane, provider.maxTileZoom);
    const level = wanted[0]?.z ?? Math.round(this.view.zoom);
    const live = new Set<string>();

    for (const tile of wanted) {
      live.add(tile.key);
      let img = this.tiles.get(tile.key);
      if (!img) {
        const url = tileUrl(provider, tile.z, tile.x, tile.y, key);
        if (url === null) continue;
        img = this.createTile(url, tile.z);
        this.tiles.set(tile.key, img);
        this.tileLayer.appendChild(img);
      }
      place(img, tile.left, tile.top, tile.size);
    }

    // The old level stays only while the new one is still arriving, and only where it is on screen.
    for (const [tileKey, img] of [...this.tiles]) {
      if (live.has(tileKey)) continue;
      const [z, x, y] = tileKey.split('/').map(Number) as [number, number, number];
      const box = tileScreenBox(this.view, this.pane, z, x, y);
      const offScreen =
        box.left > this.pane.width || box.top > this.pane.height ||
        box.left + box.size < 0 || box.top + box.size < 0;

      if (offScreen || z === level || this.pendingTiles === 0) {
        img.remove();
        this.tiles.delete(tileKey);
        continue;
      }
      place(img, box.left, box.top, box.size);
    }
  }

  private createTile(url: string, z: number): HTMLImageElement {
    this.pendingTiles += 1;
    const settle = (): void => {
      this.pendingTiles = Math.max(0, this.pendingTiles - 1);
      // The last tile of a level landing is the cue to retire the level underneath it.
      if (this.pendingTiles === 0) this.layoutTiles();
    };
    return el('img', {
      class: 'lg-frame__tile',
      dataset: { z: String(z) },
      attrs: { src: url, alt: '', draggable: 'false', decoding: 'async' },
      on: {
        load: settle,
        // A missing tile is normal at the edge of a provider's coverage — hide it rather than
        // showing a broken-image glyph, and never turn it into a page-level error.
        error: (event) => {
          (event.currentTarget as HTMLImageElement).style.visibility = 'hidden';
          settle();
        },
      },
    });
  }

  private setEmptyNote(message: string | null): void {
    this.mapEl.classList.toggle('is-empty', message !== null);
    this.emptyNote.textContent = message ?? '';
    if (message !== null) {
      for (const [tileKey, img] of this.tiles) {
        img.remove();
        this.tiles.delete(tileKey);
      }
      this.pendingTiles = 0;
    }
  }

  /**
   * The site quad, its vertex handles, the rotation handle, and the parcel ring.
   *
   * Drawn in the pane's own CSS pixels — the `viewBox` is the element's measured size — so a corner
   * is exactly where the pointer says it is, at any element size and with no image-space round trip.
   * The previous version drew into a fixed 1000×1000 box mapped through the frame's own projection,
   * which is what made a corner drag and the cursor disagree.
   */
  private drawOverlay(): void {
    const ns = 'http://www.w3.org/2000/svg';
    clear(this.overlay);
    this.overlay.setAttribute('viewBox', `0 0 ${this.pane.width} ${this.pane.height}`);
    this.overlay.setAttribute('preserveAspectRatio', 'none');

    const at = this.frame.corners.map((corner) => viewToScreen(this.view, this.pane, corner));
    const points = at.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

    // Everything outside the quad dimmed, via an even-odd fill between the viewport and the shape —
    // which keeps the surround visible rather than hidden, the whole reason the view is wider.
    const shade = document.createElementNS(ns, 'path');
    shade.setAttribute(
      'd',
      `M0,0 H${this.pane.width} V${this.pane.height} H0 Z M${at.map((p) => `${p.x},${p.y}`).join(' L')} Z`,
    );
    shade.setAttribute('fill-rule', 'evenodd');
    shade.setAttribute('class', 'lg-frame__shade');
    this.overlay.appendChild(shade);

    if (this.boundary) this.overlay.appendChild(this.boundaryRing());

    // The frame's interior is its own grab target. "Drag the middle to move it, the corners to
    // reshape it, anywhere else to move the map" is a rule that can be discovered by trying, which
    // the previous scheme — where dragging anywhere moved the map and the frame came along — was not.
    const body = document.createElementNS(ns, 'polygon');
    body.setAttribute('points', points);
    body.setAttribute('class', 'lg-frame__body');
    body.appendChild(titled('Drag to move the site frame'));
    body.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      event.preventDefault();
      this.beginFrameDrag(event);
    });
    this.overlay.appendChild(body);

    const outline = document.createElementNS(ns, 'polygon');
    outline.setAttribute('points', points);
    outline.setAttribute(
      'class',
      frameIsRectangular(this.frame) ? 'lg-frame__site' : 'lg-frame__site is-skewed',
    );
    this.overlay.appendChild(outline);

    // The rotation handle, on a stalk off the top edge's midpoint — the convention every shape
    // editor uses, and the one place a turn cannot be confused with a corner drag.
    const [tl, tr, br, bl] = at as [ScreenPoint, ScreenPoint, ScreenPoint, ScreenPoint];
    const topMid = { x: (tl.x + tr.x) / 2, y: (tl.y + tr.y) / 2 };
    const bottomMid = { x: (bl.x + br.x) / 2, y: (bl.y + br.y) / 2 };
    const length = Math.hypot(topMid.x - bottomMid.x, topMid.y - bottomMid.y) || 1;
    const handle = {
      x: topMid.x + ((topMid.x - bottomMid.x) / length) * 26,
      y: topMid.y + ((topMid.y - bottomMid.y) / length) * 26,
    };

    const stalk = document.createElementNS(ns, 'line');
    stalk.setAttribute('x1', String(topMid.x));
    stalk.setAttribute('y1', String(topMid.y));
    stalk.setAttribute('x2', String(handle.x));
    stalk.setAttribute('y2', String(handle.y));
    stalk.setAttribute('class', 'lg-frame__stalk');
    this.overlay.appendChild(stalk);

    this.overlay.appendChild(
      this.handleNode(handle.x, handle.y, 6, 'lg-frame__rotator', 'Turn the frame', (event) =>
        this.beginRotate(event),
      ),
    );

    for (const [index, point] of at.entries()) {
      this.overlay.appendChild(
        this.handleNode(
          point.x,
          point.y,
          7,
          'lg-frame__vertex',
          `Drag the ${CORNER_NAMES[index]} corner`,
          (event) => this.beginCornerDrag(event, index),
        ),
      );
    }
  }

  private handleNode(
    x: number,
    y: number,
    radius: number,
    className: string,
    label: string,
    onDown: (event: PointerEvent) => void,
  ): SVGElement {
    const ns = 'http://www.w3.org/2000/svg';
    const group = document.createElementNS(ns, 'g');
    group.setAttribute('class', className);

    // A generous invisible target under a small visible dot: the dot has to stay small enough not to
    // hide the corner it marks, and a ~18 px hit area is what makes it grabbable anyway.
    const target = document.createElementNS(ns, 'circle');
    target.setAttribute('cx', String(x));
    target.setAttribute('cy', String(y));
    target.setAttribute('r', String(Math.max(16, radius * 2.4)));
    target.setAttribute('class', 'lg-frame__hit');

    const dot = document.createElementNS(ns, 'circle');
    dot.setAttribute('cx', String(x));
    dot.setAttribute('cy', String(y));
    dot.setAttribute('r', String(radius));

    group.append(target, dot, titled(label));
    group.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      event.preventDefault();
      onDown(event);
    });
    return group;
  }

  /** The parcel ring, in the same screen pixels as everything else on the overlay. */
  private boundaryRing(): SVGElement {
    const ns = 'http://www.w3.org/2000/svg';
    const polygon = document.createElementNS(ns, 'polygon');
    polygon.setAttribute(
      'points',
      (this.boundary?.ring ?? [])
        .map((point) => viewToScreen(this.view, this.pane, point))
        .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        .join(' '),
    );
    polygon.setAttribute(
      'class',
      `lg-frame__parcel ${this.boundary?.simulated ? 'is-simulated' : 'is-real'}`,
    );
    return polygon;
  }

  /**
   * What the gestures do, above the map they happen on.
   *
   * Corner dragging is not discoverable from a rectangle with dots on it — the dots read as
   * decoration until someone happens to try — and neither is "the middle moves it". One line, stated
   * before the thing it describes, is the cheapest fix, and it changes with the frame's state so it
   * stays worth reading rather than becoming furniture.
   */
  private refreshHowTo(): void {
    clear(this.howto);
    const rectangular = frameIsRectangular(this.frame);
    const turned = Math.round(this.frame.headingDegrees) !== 0;
    const offScreen = this.frameIsOffScreen();

    append(
      this.howto,
      el('span', { class: 'lg-frame__howtoicon', html: icon('move', 14) }),
      el('span', {
        class: 'lg-frame__howtotext',
        text: offScreen
          ? 'The site frame is off screen. “Show the frame” brings the map back to it.'
          : rectangular
            ? 'Drag inside the frame to move it, the green corners to reshape it, the ○ to turn it. Drag the map to pan, scroll to zoom.'
            : 'Reshaped. The quad is squared up to fill the site on capture — “Square up” restores a true rectangle.',
      }),
      offScreen &&
        dialogButton({
          label: 'Show the frame',
          icon: 'focus',
          onClick: () => this.lookAtFrame(),
        }),
      !rectangular &&
        el('span', {
          class: 'lg-frame__howtowarn',
          text: `skew ${(frameSkew(this.frame) * 100).toFixed(0)}%`,
          title:
            'How far the frame has drifted from a rectangle. Capturing rectifies it to fill the ' +
            'square site, which stretches the ground unevenly — fine for framing an awkward plot, ' +
            'worth knowing about when measuring off the result.',
        }),
      turned &&
        el('span', {
          class: 'lg-frame__howtoturn',
          text: `${Math.round(this.frame.headingDegrees)}°`,
          title: 'Turn from north-up. Rotation is exact — no ground is stretched.',
        }),
    );

    const chosen = imageryProvider(this.providerId);
    const drawn = browseProvider(chosen);
    const geocoder = this.results.some((r) => !r.exact) ? `  ·  ${GEOCODER_ATTRIBUTION}` : '';
    this.attribution.textContent =
      drawn.id === chosen.id
        ? `${chosen.attribution}${geocoder}`
        : `Browsing on ${drawn.attribution} — ${chosen.name} may not be drawn in a map like this one, ` +
          `so the capture will come from ${chosen.name} at the same coordinates.${geocoder}`;
  }

  /** Whether the frame has been panned entirely out of the pane, so the user has lost it. */
  private frameIsOffScreen(): boolean {
    const at = this.frame.corners.map((corner) => viewToScreen(this.view, this.pane, corner));
    const margin = 8;
    return (
      at.every((p) => p.x < margin) ||
      at.every((p) => p.x > this.pane.width - margin) ||
      at.every((p) => p.y < margin) ||
      at.every((p) => p.y > this.pane.height - margin)
    );
  }

  private frameControls(): HTMLElement {
    const provider = imageryProvider(this.providerId);

    const sizes = el('div', { class: 'lg-chips lg-frame__sizes' });
    for (const size of FRAME_SIZE_PRESETS) {
      sizes.appendChild(
        el('button', {
          class: `lg-chip${Math.abs(this.sizeMeters - size) < 1 ? ' is-active' : ''}`,
          text: `${size} m`,
          title: `${formatLength(size, 'feet')} across`,
          attrs: { type: 'button', 'data-autofocus': 'skip' },
          on: { click: () => this.setSize(size) },
        }),
      );
    }

    const providerSelect = el('select', {
      class: 'lg-input',
      attrs: { 'aria-label': 'Imagery provider' },
      on: {
        change: (event) => {
          this.providerId = (event.target as HTMLSelectElement).value;
          this.frame = { ...this.frame, providerId: this.providerId };
          this.dropTiles();
          this.render();
        },
      },
    });
    for (const option of IMAGERY_PROVIDERS) {
      providerSelect.appendChild(
        el('option', {
          text: option.coverage === 'united-states' ? `${option.name} (US only)` : option.name,
          attrs: { value: option.id, selected: option.id === this.providerId },
        }),
      );
    }
    providerSelect.value = this.providerId;

    const edges = frameEdgeMeters(this.frame);
    const rectangular = frameIsRectangular(this.frame);

    return el(
      'div',
      { class: 'lg-frame__controls' },
      el(
        'div',
        { class: 'lg-frame__readout' },
        el('span', { class: 'lg-frame__coords', text: describeFrame(this.frame) }),
        el('span', {
          class: 'lg-dialog__hint',
          text: rectangular
            ? `${formatLength(this.frame.widthMeters, 'feet')} × ${formatLength(this.frame.lengthMeters, 'feet')}`
            : `edges ${edges.top.toFixed(1)} / ${edges.right.toFixed(1)} / ${edges.bottom.toFixed(1)} / ${edges.left.toFixed(1)} m  ·  ` +
              `rectified to ${Math.round(this.frame.widthMeters)} × ${Math.round(this.frame.lengthMeters)} m on capture`,
        }),
      ),
      el('span', { class: 'lg-field__label', text: 'Shape' }),
      el(
        'div',
        { class: 'lg-frame__actions' },
        dialogButton({
          label: 'Square up',
          icon: 'grid',
          title:
            'Rebuild a true rectangle from the quad’s centre, mean edge lengths and top-edge bearing — ' +
            'keeps the orientation and the size, drops the resampling a skewed frame costs.',
          onClick: () => this.squareUp(),
          disabled: rectangular,
        }),
        dialogButton({
          label: 'Reset to north-up',
          icon: 'rotate',
          title: 'Back to an unturned square at the current size',
          onClick: () => this.resetShape(),
          disabled: rectangular && Math.round(this.frame.headingDegrees) === 0,
        }),
        dialogButton({
          label: 'Centre on the view',
          icon: 'focus',
          title: 'Move the frame to the middle of what is on screen',
          onClick: () => this.centreFrameOnView(),
        }),
      ),
      el('span', { class: 'lg-field__label', text: 'Site size' }),
      sizes,
      el('span', { class: 'lg-field__label', text: 'Imagery' }),
      providerSelect,
      el('p', { class: 'lg-frame__licence', text: provider.licenceNote }),
      provider.needsKey && this.keyRow(provider.id, provider.keyLabel ?? 'Key', provider.keyHint),
    );
  }

  private keyRow(providerId: string, label: string, hint?: string): HTMLElement {
    const field = textInput({
      value: '',
      placeholder: this.keys.has(providerId) ? 'Stored — enter a new one to replace it' : label,
      ariaLabel: label,
      onInput: (value) => {
        if (value.trim().length === 0) return;
        this.keys.set(providerId, value);
        this.dropTiles();
        this.render();
      },
    });
    field.type = 'password';
    return el(
      'div',
      { class: 'lg-frame__key' },
      field,
      hint !== undefined && el('span', { class: 'lg-dialog__hint', text: hint }),
    );
  }

  /* ------------------------------- What is known ------------------------------- */

  /**
   * Public data about this ground.
   *
   * Sources that are not wired yet are listed rather than hidden, greyed, with what each would take
   * as its tooltip. Hiding them would make the seam invisible and the next person would rebuild it;
   * showing them as if they worked would be a lie. Listing them as declared intent is the honest
   * third option, and it costs one disabled row each.
   */
  private knownSection(): HTMLElement {
    const section = el('div', { class: 'lg-frame__known' });

    section.appendChild(el('h4', { class: 'lg-section__title', text: 'Property boundary' }));
    section.appendChild(
      el(
        'div',
        { class: 'lg-frame__actions' },
        dialogButton({
          label: this.boundary?.simulated ? 'Re-roll simulated lot' : 'Simulate a lot',
          icon: 'grid',
          title:
            'Invents a plausible lot outline from these coordinates. Deterministic, and never a real ' +
            'property line — it is here so the frame-fitting and the overlay are real while the ' +
            'parcel services are not yet wired.',
          onClick: () => this.useSimulatedParcel(),
        }),
        this.fileButton('Boundary file…', '.json,.geojson', (name, text) => this.loadBoundary(name, text)),
        this.boundary !== null &&
          dialogButton({
            label: 'Fit frame to lot',
            icon: 'focus',
            title: 'Re-centre and resize the frame so the whole parcel sits inside it with a margin',
            onClick: () => this.fitToBoundary(),
          }),
        this.boundary !== null &&
          dialogButton({ label: 'Clear', onClick: () => this.setBoundary(null) }),
      ),
    );
    if (this.boundary) {
      section.appendChild(
        el('p', {
          class: `lg-frame__datum${this.boundary.simulated ? ' is-simulated' : ''}`,
          text: `${this.boundary.simulated ? 'SIMULATED — not a real property line. ' : ''}${this.boundary.label} · ${describeArea(this.boundary.areaSquareMeters)}`,
        }),
      );
    }
    section.appendChild(this.plannedRow('parcel'));

    section.appendChild(el('h4', { class: 'lg-section__title', text: 'Topography' }));
    section.appendChild(
      el(
        'div',
        { class: 'lg-frame__actions' },
        this.fileButton('Elevation file…', '.asc,.grd,.xyz,.csv,.txt,.json,.geojson', (name, text) =>
          this.loadTopography(name, text),
        ),
        this.hasTopography() &&
          dialogButton({
            label: 'Use as the terrain stage',
            icon: 'topo',
            primary: true,
            title: 'Replaces stage 2 with these measured readings, so nothing about the ground is guessed',
            onClick: () => this.useTopography(),
          }),
        this.hasTopography() && dialogButton({ label: 'Clear', onClick: () => this.clearTopography() }),
      ),
    );
    section.appendChild(this.topographyReadout());
    section.appendChild(this.plannedRow('elevation'));

    section.appendChild(el('h4', { class: 'lg-section__title', text: 'Buildings, walls & fences' }));
    section.appendChild(
      el(
        'div',
        { class: 'lg-frame__actions' },
        dialogButton({
          label: this.fetchingStructures ? 'Asking OpenStreetMap…' : 'Fetch from OpenStreetMap',
          icon: 'layers',
          title: 'Surveyed footprints and heights for everything mapped inside this frame',
          disabled: this.fetchingStructures,
          onClick: () => void this.fetchStructures(),
        }),
        this.structures !== null &&
          dialogButton({
            label: 'Use as the structures stage',
            icon: 'new',
            primary: true,
            title: 'Replaces stage 5 with measured footprints, so nothing about the outlines is guessed',
            onClick: () => this.useStructures(),
          }),
        this.structures !== null &&
          dialogButton({ label: 'Clear', onClick: () => this.clearStructures() }),
      ),
    );
    section.appendChild(this.structuresReadout());

    return section;
  }

  /**
   * What the import found, before anything is committed to a stage.
   *
   * Counts rather than a bare "ready", because the two numbers that decide whether this is worth
   * using — how many buildings carry a real height, and how much was thrown away — are exactly the
   * ones a summary would hide.
   */
  private structuresReadout(): HTMLElement {
    const found = this.structures;
    if (!found) {
      return el('p', {
        class: 'lg-dialog__hint',
        text:
          'Footprints are surveyed and most carry a height or a storey count, so stage 5 stops ' +
          'tracing roofs out of a photograph. Roof shape and colour are not in the data and stay ' +
          'stage 5’s job.',
      });
    }

    const { report } = found;
    const buildings = report.accepted.filter((trace) => trace.kind === 'building').length;
    const barriers = report.accepted.length - buildings;

    return el(
      'p',
      { class: 'lg-frame__datum' },
      el('span', {
        text:
          `${buildings} building${buildings === 1 ? '' : 's'}, ${barriers} wall/fence run${barriers === 1 ? '' : 's'}` +
          ` · ${Math.round(report.heightCoverage * 100)}% of heights measured`,
      }),
      el('span', {
        class: report.accepted.length === 0 ? 'lg-frame__warn' : 'lg-dialog__hint',
        text:
          report.accepted.length === 0
            ? 'Nothing built is mapped inside this frame. That means no data, not no buildings — run stage 5 normally.'
            : `${report.fetched} way(s) fetched, ${report.rejected.length} rejected. Everything mapped inside the ` +
              'frame is included, neighbours as well — tag what belongs to the site.',
      }),
    );
  }

  private topographyReadout(): HTMLElement {
    if (this.grid) {
      const coverage = gridCoverage(this.grid, this.frame);
      const coarse = coverage.resolutionRatio > 1.5;
      return el(
        'p',
        { class: 'lg-frame__datum' },
        el('span', { text: `${this.grid.label} — ${describeGrid(this.grid)}` }),
        el('span', {
          class: coverage.cellsInFrame === 0 ? 'lg-frame__warn' : 'lg-dialog__hint',
          text:
            coverage.cellsInFrame === 0
              ? 'None of this grid falls inside the frame. Is it the right site, or the wrong projection?'
              : `${coverage.cellsInFrame.toLocaleString()} cells inside the frame` +
                (coarse
                  ? ` · cells are coarse for a site this size — this describes the hill the site sits on, not the fall across it.`
                  : ''),
        }),
      );
    }
    if (this.spotHeights) {
      return el('p', {
        class: 'lg-frame__datum',
        text: `${this.topographyLabel} — ${this.spotHeights.length} spot heights inside the frame`,
      });
    }
    return el('p', {
      class: 'lg-dialog__hint',
      text:
        'An Esri ASCII grid (.asc) from USGS, OpenTopography or QGIS; an XYZ or CSV of levels; or a ' +
        'GeoJSON of spot heights. Measured ground beats a model’s guess at it every time.',
    });
  }

  private plannedRow(kind: 'parcel' | 'elevation'): HTMLElement {
    const planned = SITE_DATA_SOURCES.filter(
      (source) => source.kind === kind && source.status === 'planned',
    );
    const row = el('div', { class: 'lg-frame__planned' });
    append(row, el('span', { class: 'lg-frame__plannedlabel', text: 'Not wired yet' }));
    for (const source of planned) {
      row.appendChild(
        el('span', {
          class: 'lg-frame__plannedchip',
          text: source.name,
          title: `${source.summary}\n\nWhat it would take: ${source.wiringNote ?? '—'}`,
        }),
      );
    }
    return row;
  }

  private captureRow(): HTMLElement {
    const provider = imageryProvider(this.providerId);
    const blocked = provider.needsKey && !this.keys.has(this.providerId);

    const capture = dialogButton({
      label: this.capturing ? 'Capturing…' : 'Use this frame',
      primary: true,
      icon: 'camera',
      onClick: () => void this.capture(),
      disabled: this.capturing || blocked,
    });
    capture.title = blocked
      ? `Add a ${provider.keyLabel ?? 'key'} first, or switch providers.`
      : 'Fetch this rectangle at full resolution and use it as the reference photograph';

    return el(
      'div',
      { class: 'lg-frame__capture' },
      capture,
      this.capturing && dialogButton({ label: 'Cancel', onClick: () => this.abort?.abort() }),
      this.status !== null &&
        el('span', {
          class: 'lg-frame__status',
          text: this.status.message,
          dataset: { tone: this.status.tone },
        }),
    );
  }

  /* ---------------------------------- Gestures ---------------------------------- */

  /**
   * A drag that started on the map itself — so, outside the frame — moves the map.
   *
   * The view is its own state, so this changes nothing about the site: the frame stays on the ground
   * it was placed on and slides out from under you, which is the behaviour of every map that has a
   * marker on it. `panView` does the arithmetic in the view's own pixels, which is why the ground
   * lands exactly where it was released — the old code derived metres-per-pixel from a requested
   * extent that the integer-zoom snap had already changed, so a drag moved between half and all of
   * what it should.
   */
  private onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    const start = { x: event.clientX, y: event.clientY };
    const from = this.view;

    this.captureGesture(event, (moveEvent) => {
      this.view = panView(from, moveEvent.clientX - start.x, moveEvent.clientY - start.y);
      this.layout();
    });
  }

  /**
   * Wheel and pinch zoom the *map*, about the cursor.
   *
   * It used to resize the site instead, clamped to 10–200 m and snapped to integer zooms — so several
   * ticks did nothing and then one doubled the ground, while each tick refetched the imagery. Two
   * different things had been bound to one gesture; this is the one people mean by "zoom".
   */
  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    const rect = this.mapEl.getBoundingClientRect();
    if (rect.width <= 0) return;

    // Trackpads report small pixel deltas and mice report ~100 at a time; both want the same feel,
    // so the step is proportional and capped rather than a fixed number of zoom levels.
    const lines = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 400 : 1;
    const step = clamp((-event.deltaY * lines) / 320, -1, 1);
    const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };

    this.view = zoomViewAt(
      this.view,
      this.pane,
      clamp(this.view.zoom + step, MIN_VIEW_ZOOM, MAX_VIEW_ZOOM),
      anchor,
    );
    this.layout();
  }

  /** Drag the frame's interior to slide the site over the ground, keeping its shape exactly. */
  private beginFrameDrag(event: PointerEvent): void {
    if (event.button !== 0) return;
    const start = { x: event.clientX, y: event.clientY };
    const from = this.frame;
    const metersPerPixel = viewMetersPerPixel(this.view);
    let moved = false;

    this.captureGesture(
      event,
      (moveEvent) => {
        const dx = moveEvent.clientX - start.x;
        const dy = moveEvent.clientY - start.y;
        if (!moved && Math.abs(dx) < DRAG_SLOP_PIXELS && Math.abs(dy) < DRAG_SLOP_PIXELS) return;
        moved = true;
        this.frame = panFrame(from, dx * metersPerPixel, dy * metersPerPixel);
        this.layout();
      },
      () => {
        if (!moved) return;
        // A parcel belongs to the ground it was invented for, so moving the frame invalidates it.
        if (this.boundary?.simulated) this.boundary = null;
        this.commitFrame();
      },
    );
  }

  /**
   * Drag one vertex.
   *
   * The pointer's own position is converted straight to a coordinate through the view, so the corner
   * lands exactly under the cursor. `moveCorner` rejects a drag that would make the quad concave,
   * which shows up as the corner simply refusing to follow past the fold — the one place that
   * constraint is cheap to feel rather than to explain.
   *
   * Nothing here touches the view. That is the whole of the "the skew is limited and it fights me"
   * fix: the frame used to define the view drawn from it, so widening the quad zoomed the map out
   * under the cursor and the corner chased a target that kept moving away.
   */
  private beginCornerDrag(event: PointerEvent, index: number): void {
    if (event.button !== 0) return;
    let changed = false;

    this.captureGesture(
      event,
      (moveEvent) => {
        const at = this.pointerInPane(moveEvent);
        if (!at) return;
        const next = moveCorner(this.frame, index, screenToView(this.view, this.pane, at));
        if (next === this.frame) return;
        this.frame = next;
        this.sizeMeters = Math.round(this.frame.widthMeters);
        changed = true;
        this.layout();
      },
      () => {
        if (changed) this.commitFrame();
      },
    );
  }

  /**
   * Drag the stalk handle to turn the frame. Exact — rotation stretches no ground.
   *
   * The angle is measured from the *frame's* centre, not the pane's. Those were the same point only
   * while the view was pinned to the frame; now that the map pans independently, using the element
   * centre would turn the frame around a point that has nothing to do with it.
   */
  private beginRotate(event: PointerEvent): void {
    if (event.button !== 0) return;
    let changed = false;

    this.captureGesture(
      event,
      (moveEvent) => {
        const at = this.pointerInPane(moveEvent);
        if (!at) return;
        const centre = viewToScreen(this.view, this.pane, this.frame.center);
        // Adding 90° converts "direction of image-up" into the frame's clockwise turn from north-up.
        const degrees = (Math.atan2(at.y - centre.y, at.x - centre.x) * 180) / Math.PI + 90;
        // Shift snaps to 15°, for the common case of squaring to a street.
        const snapped = moveEvent.shiftKey ? Math.round(degrees / 15) * 15 : Math.round(degrees);
        this.frame = rotateFrame(this.frame, snapped);
        changed = true;
        this.layout();
      },
      () => {
        if (changed) this.commitFrame();
      },
    );
  }

  /**
   * One pointer-capture lifecycle, so every gesture below tears down the same way.
   *
   * Listeners go on `window` rather than the map: a corner dragged past the pane's edge — which is
   * exactly how you frame a plot that spills off screen — otherwise stops receiving moves the moment
   * it leaves, and the frame freezes mid-drag for no reason the user can see.
   */
  private captureGesture(
    event: PointerEvent,
    onMove: (event: PointerEvent) => void,
    onEnd?: () => void,
  ): void {
    try {
      this.mapEl.setPointerCapture(event.pointerId);
    } catch {
      // Capture is a convenience; the window listeners below are what actually carry the gesture.
    }
    this.mapEl.classList.add('is-dragging');

    const move = (moveEvent: PointerEvent): void => {
      if (moveEvent.pointerId !== event.pointerId) return;
      onMove(moveEvent);
    };
    const end = (endEvent: PointerEvent): void => {
      if (endEvent.pointerId !== event.pointerId) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      this.mapEl.classList.remove('is-dragging');
      onEnd?.();
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }

  /** A pointer event as a position inside the map pane, or null if the pane has no size yet. */
  private pointerInPane(event: PointerEvent): ScreenPoint | null {
    const rect = this.mapEl.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  private onPaneResize(entries: readonly ResizeObserverEntry[]): void {
    const box = entries[0]?.contentRect;
    if (!box || box.width <= 0 || box.height <= 0) return;
    this.pane = { width: box.width, height: box.height };
    // The opening view is fitted to the frame once the pane's real size is known. After that the
    // view belongs to the user, and a resize must not throw away where they had browsed to.
    if (!this.paneMeasured) {
      this.paneMeasured = true;
      this.view = viewForFrame(this.frame, this.pane);
    }
    this.layout();
  }

  /* ---------------------------------- Behaviour ---------------------------------- */

  /** The frame changed and the change is finished: tell the host and redraw the readouts. */
  private commitFrame(): void {
    this.deps.onChange?.();
    this.render();
  }

  private async search(): Promise<void> {
    if (this.query.trim().length === 0) return;
    this.searching = true;
    this.status = null;
    this.render();
    try {
      const results = await findPlace(this.query);
      this.results = results;
      if (results.length === 0) {
        this.status = { message: 'Nothing matched. Try a fuller address, or paste coordinates.', tone: 'error' };
      } else if (results.length === 1) {
        this.goTo((results[0] as PlaceResult).at);
        return;
      }
    } catch (error) {
      this.status = {
        message: error instanceof GeocodeError ? error.message : 'The address lookup failed.',
        tone: 'error',
      };
    } finally {
      this.searching = false;
      this.render();
    }
  }

  private goTo(center: LatLng): void {
    this.frame = createFrame({ center, sizeMeters: this.sizeMeters, providerId: this.providerId });
    this.view = viewForFrame(this.frame, this.pane);
    this.results = [];
    this.status = null;
    // A parcel belongs to the place it was invented for, so moving the map invalidates it.
    if (this.boundary?.simulated) this.boundary = null;
    this.commitFrame();
  }

  /** Bring the map back to the frame without moving the frame. */
  private lookAtFrame(): void {
    this.view = viewForFrame(this.frame, this.pane);
    this.layout();
  }

  /** Move the frame to the middle of what is on screen, without changing its shape. */
  private centreFrameOnView(): void {
    const from = this.frame.center;
    const metersPerPixel = viewMetersPerPixel(this.view);
    const at = viewToScreen(this.view, this.pane, from);
    this.frame = panFrame(
      this.frame,
      (this.pane.width / 2 - at.x) * metersPerPixel,
      (this.pane.height / 2 - at.y) * metersPerPixel,
    );
    if (this.boundary?.simulated) this.boundary = null;
    this.commitFrame();
  }

  /**
   * A size preset makes the frame square again at that size, keeping its turn and its place.
   *
   * Resizing a dragged quad has no single right answer — scaling it about its centroid keeps the
   * skew, which is rarely what someone reaching for "60 m" wants. Squaring off is the predictable
   * reading, and the shape is one drag away again.
   */
  private setSize(sizeMeters: number): void {
    this.sizeMeters = clamp(sizeMeters, MIN_FRAME_METERS, MAX_FRAME_METERS);
    this.frame = createFrame({
      center: this.frame.center,
      sizeMeters: this.sizeMeters,
      providerId: this.providerId,
      headingDegrees: this.frame.headingDegrees,
    });
    this.commitFrame();
  }

  private squareUp(): void {
    this.frame = squareUpFrame(this.frame);
    this.sizeMeters = Math.round(this.frame.widthMeters);
    this.commitFrame();
  }

  private resetShape(): void {
    this.frame = createFrame({
      center: this.frame.center,
      sizeMeters: this.sizeMeters,
      providerId: this.providerId,
    });
    this.commitFrame();
  }

  private useSimulatedParcel(): void {
    // Re-rolling moves the centre a few metres so the hash changes; the point is to show the shape
    // is invented, not to pretend a second opinion exists.
    const frame = this.boundary?.simulated ? panFrame(this.frame, 3.7, -2.3) : this.frame;
    this.frame = frame;
    this.setBoundary(simulateParcel(frame));
  }

  private fitToBoundary(): void {
    const bounds = this.boundary ? boundaryBounds(this.boundary) : null;
    if (!bounds) return;
    this.frame = frameForBounds(bounds, this.providerId);
    this.sizeMeters = Math.round(this.frame.widthMeters);
    this.view = viewForFrame(this.frame, this.pane);
    this.commitFrame();
  }

  private setBoundary(boundary: ParcelBoundary | null): void {
    this.boundary = boundary;
    this.commitFrame();
  }

  private loadBoundary(name: string, text: string): void {
    try {
      this.boundary = measureBoundary(this.frame, parseBoundaryGeoJson(name, text));
      this.status = { message: `Read a boundary from ${name}.`, tone: 'info' };
    } catch (error) {
      this.status = { message: describeError(error), tone: 'error' };
    }
    this.commitFrame();
  }

  private loadTopography(name: string, text: string): void {
    try {
      const imported = parseTopographyFile(name, text);
      this.topographyLabel = name;
      if (imported.kind === 'grid') {
        this.grid = imported.grid;
        this.spotHeights = null;
      } else {
        this.grid = null;
        this.spotHeights = spotHeightsToControlPoints(imported.points, this.frame);
        if (this.spotHeights.length === 0) {
          this.status = {
            message: `${name} read fine, but none of its ${imported.points.length} readings fall inside this frame.`,
            tone: 'error',
          };
          this.spotHeights = null;
        }
      }
      if (this.hasTopography() && this.status?.tone !== 'error') {
        this.status = { message: `Read topography from ${name}.`, tone: 'info' };
      }
    } catch (error) {
      this.status = { message: describeError(error), tone: 'error' };
    }
    this.render();
  }

  /**
   * Ask Overpass for everything built inside the frame.
   *
   * Mirrors are tried in order because the public instances rate-limit independently and a busy one
   * answers with a `remark` rather than an HTTP error — which {@link parseOverpass} turns into a
   * thrown message rather than an empty, plausible-looking result.
   */
  private async fetchStructures(): Promise<void> {
    this.fetchingStructures = true;
    this.status = { message: 'Asking OpenStreetMap what is built here…', tone: 'info' };
    this.render();

    const bounds = frameBounds(this.frame);
    const query = overpassQuery(bounds, { barriers: true });
    let lastError: unknown = null;

    for (const endpoint of OVERPASS_MIRRORS) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ data: query }).toString(),
        });
        if (!response.ok) throw new Error(`${endpoint} refused the query (HTTP ${response.status}).`);

        const ways = parseOverpass(await response.json());
        const found = osmToStructures(ways, { frame: this.frame, query, bounds });

        this.structures = found;
        this.status = {
          message:
            found.report.accepted.length === 0
              ? 'OpenStreetMap has nothing built mapped inside this frame.'
              : `Measured ${found.report.accepted.length} structure(s) from ${found.report.fetched} way(s).`,
          tone: found.report.accepted.length === 0 ? 'error' : 'info',
        };
        this.fetchingStructures = false;
        this.render();
        return;
      } catch (error) {
        lastError = error;
      }
    }

    this.structures = null;
    this.fetchingStructures = false;
    this.status = { message: `OpenStreetMap could not be reached — ${describeError(lastError)}`, tone: 'error' };
    this.render();
  }

  private clearStructures(): void {
    this.structures = null;
    this.render();
  }

  /**
   * Hand the measured structures to stage 5.
   *
   * The report goes with them. A supplied artifact that cannot be audited is only a different kind
   * of black box, and the whole argument for measuring rather than inferring is that the provenance
   * survives to where somebody can check it.
   */
  private useStructures(): void {
    const found = this.structures;
    if (!found || found.report.accepted.length === 0) {
      this.status = { message: 'Nothing measured to hand over yet.', tone: 'error' };
      this.render();
      return;
    }

    this.deps.onStructures?.(found.artifact, describeStructureReport(found.report), this.frame);
    this.status = {
      message:
        `Stage 5 replaced with ${found.report.accepted.length} measured structure(s). Roof form and ` +
        'colour are still open — re-run stage 5 to have those read off the photograph.',
      tone: 'info',
    };
    this.render();
  }

  private hasTopography(): boolean {
    return this.grid !== null || (this.spotHeights?.length ?? 0) > 0;
  }

  private clearTopography(): void {
    this.grid = null;
    this.spotHeights = null;
    this.topographyLabel = '';
    this.render();
  }

  /**
   * Hand the measured ground to stage 2.
   *
   * A **grid** produces both halves: the lattice the compiler bakes, and the thinned control points
   * that describe the landform to stages 3–5. Scattered **spot heights** produce only the latter —
   * a dozen levels across a garden are readings, not a surface, and gridding them would invent the
   * ground in between and then label it measured.
   */
  private useTopography(): void {
    const measured = this.grid ? gridToMeasuredGround(this.grid, this.frame) : null;
    const points = this.grid
      ? gridToControlPoints(this.grid, this.frame)
      : (this.spotHeights ?? []);

    if (points.length === 0 && !measured) {
      this.status = {
        message: 'Nothing usable inside the frame — the readings are flat, or they are elsewhere.',
        tone: 'error',
      };
      this.render();
      return;
    }

    this.deps.onTerrain?.(topographyToArtifact(points, this.topographyLabel, measured), this.frame);
    this.status = {
      message: measured
        ? `Stage 2 replaced with ${measured.cols} × ${measured.rows} measured samples at ` +
          `${measured.spacingXMeters.toFixed(2)} m from ${this.topographyLabel} — baked directly, ` +
          'not thinned to control points.'
        : `Stage 2 replaced with ${points.length} measured readings from ${this.topographyLabel}.`,
      tone: 'info',
    };
    this.render();
  }

  private async capture(): Promise<void> {
    const provider = imageryProvider(this.providerId);
    this.capturing = true;
    this.status = { message: 'Fetching the frame at full resolution…', tone: 'info' };
    this.abort = new AbortController();
    this.render();

    try {
      const file = await captureFrameImage(
        this.frame,
        provider,
        this.keys.get(this.providerId) ?? undefined,
        this.abort.signal,
      );
      this.deps.onCapture(file, this.frame);
      this.status = {
        message: `Captured ${Math.round(this.frame.widthMeters)} × ${Math.round(this.frame.lengthMeters)} m at ${this.frame.metersPerPixel.toFixed(2)} m per pixel. The site extent is now measured, not guessed.`,
        tone: 'info',
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        this.status = { message: 'Cancelled.', tone: 'info' };
      } else {
        this.status = { message: describeError(error), tone: 'error' };
      }
    } finally {
      this.capturing = false;
      this.abort = null;
      this.render();
    }
  }

  /** Throw away the tile layer — the imagery source changed, so every tile on screen is stale. */
  private dropTiles(): void {
    for (const [key, img] of this.tiles) {
      img.remove();
      this.tiles.delete(key);
    }
    this.pendingTiles = 0;
  }

  /** A button that opens a file picker and hands back the text. */
  private fileButton(
    label: string,
    accept: string,
    onText: (name: string, text: string) => void,
  ): HTMLElement {
    const input = el('input', {
      attrs: { type: 'file', accept },
      style: { display: 'none' },
      on: {
        change: (event) => {
          const target = event.target as HTMLInputElement;
          const file = target.files?.[0];
          target.value = '';
          if (!file) return;
          void file
            .text()
            .then((text) => onText(file.name, text))
            .catch(() => {
              this.status = { message: `${file.name} could not be read.`, tone: 'error' };
              this.render();
            });
        },
      },
    });
    const button = dialogButton({ label, icon: 'open', onClick: () => input.click() });
    return el('span', { class: 'lg-frame__filebtn' }, button, input);
  }
}

/** Position a tile without touching layout — transforms compose on the compositor, `left` does not. */
function place(img: HTMLImageElement, left: number, top: number, size: number): void {
  img.style.transform = `translate3d(${left.toFixed(2)}px, ${top.toFixed(2)}px, 0)`;
  img.style.width = `${size.toFixed(2)}px`;
  img.style.height = `${size.toFixed(2)}px`;
}

/** An SVG `<title>`, which is how a shape gets a tooltip and an accessible name at once. */
function titled(label: string): SVGTitleElement {
  const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
  title.textContent = label;
  return title;
}

function describeError(error: unknown): string {
  if (error instanceof ImageryError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
